"use client";

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

/**
 * Scroll-reveal wrapper — the ONLY client boundary server renderers need for
 * entrance motion. Children remain server-rendered (ReactNode passthrough).
 *
 * Pairs with the `.apa-reveal` CSS in apa-theme.css: the element starts hidden
 * (only when scripting is enabled, with a 2.5s failsafe reveal), and this
 * component adds `is-visible` the first time it enters the viewport. `delay`
 * staggers siblings via the `--reveal-delay` custom property. Under
 * prefers-reduced-motion the CSS kill-switch keeps everything visible/static.
 */
export function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  /** Transition delay in ms (stagger siblings: i * 100). */
  delay?: number;
  as?: "div" | "section" | "li" | "span";
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("is-visible");
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            observer.unobserve(el);
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      className={["apa-reveal", className].filter(Boolean).join(" ")}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </Tag>
  );
}
